import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { eggWatchAdminAPI, FeedFormulaPerformance } from '../../../services/eggWatchAdminAPI';
import EggWatchFeedPerformanceTable from './EggWatchFeedPerformanceTable';
import './EggWatchAdmin.css';

const EggWatchFeedPerformance: React.FC = () => {
  const [data, setData] = useState<FeedFormulaPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [selectedFormula, setSelectedFormula] = useState<FeedFormulaPerformance | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await eggWatchAdminAPI.getFeedPerformance({ period });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load feed performance:', err);
    } finally {
      setLoading(false);
    }
  };

  const chartData = data.map(f => ({
    name: f.formula_name.length > 15 ? f.formula_name.slice(0, 15) + '...' : f.formula_name,
    fullName: f.formula_name,
    avgRate: f.avg_lay_rate,
    vsExpected: f.vs_expected,
  }));

  const barColor = (vs: number) => {
    if (vs >= 5) return '#059669';
    if (vs >= 0) return '#2563eb';
    if (vs >= -5) return '#d97706';
    return '#dc2626';
  };

  return (
    <div className="egg-watch-admin">
      <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch')}>
        <ArrowLeft size={16} /> Back to Egg Watch
      </button>

      <div className="ewa-header">
        <div className="ewa-header-title">
          <TrendingUp className="ewa-header-icon" />
          <div>
            <h1>Feed Performance Analysis</h1>
            <p className="ewa-header-subtitle">Compare feed formulas across all Egg Watch farms</p>
          </div>
        </div>
        <div className="ewa-header-actions">
          <div className="ewa-chart-range-selector">
            {[
              { value: '7d', label: '7 Days' },
              { value: '14d', label: '14 Days' },
              { value: '30d', label: '30 Days' },
              { value: '90d', label: '90 Days' },
            ].map(p => (
              <button
                key={p.value}
                className={period === p.value ? 'active' : ''}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Insight Banner */}
      {data.length > 0 && (
        <div className="ewa-insight-banner">
          <Info size={20} />
          <div>
            <strong>{data.length}</strong> feed formula{data.length !== 1 ? 's' : ''} tracked across{' '}
            <strong>{data.reduce((s, f) => s + f.farms_using, 0)}</strong> farms and{' '}
            <strong>{data.reduce((s, f) => s + f.total_hens, 0).toLocaleString()}</strong> hens.
            {data.some(f => f.vs_expected >= 5) && (
              <> Top performer: <strong>{data.reduce((best, f) => f.vs_expected > best.vs_expected ? f : best).formula_name}</strong>.</>
            )}
          </div>
        </div>
      )}

      {/* Comparison Chart */}
      <div className="ewa-card">
        <div className="ewa-card-header">
          <h2>Lay Rate Comparison</h2>
        </div>
        <div className="ewa-card-content">
          {loading ? (
            <div className="ewa-loading-state">Loading...</div>
          ) : chartData.length === 0 ? (
            <div className="ewa-empty-state">No feed performance data</div>
          ) : (
            <div className="ewa-chart-container large">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name === 'avgRate' ? 'Avg Lay Rate' : name]}
                    labelFormatter={(label: string, payload: readonly any[]) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Bar dataKey="avgRate" name="Avg Lay Rate" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={barColor(entry.vsExpected)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Full Table */}
      <div className="ewa-card">
        <div className="ewa-card-header">
          <h2>Detailed Performance</h2>
        </div>
        <div className="ewa-card-content">
          {loading ? (
            <div className="ewa-loading-state">Loading...</div>
          ) : (
            <EggWatchFeedPerformanceTable
              data={data}
              onRowClick={(id) => {
                const f = data.find(d => d.formula_id === id);
                setSelectedFormula(f || null);
              }}
            />
          )}
        </div>
      </div>

      {/* Breed Performance Detail */}
      {selectedFormula && Object.keys(selectedFormula.breed_performance).length > 0 && (
        <div className="ewa-card">
          <div className="ewa-card-header">
            <h2>Breed Performance — {selectedFormula.formula_name}</h2>
            <button className="ewa-btn-small secondary" onClick={() => setSelectedFormula(null)}>Close</button>
          </div>
          <div className="ewa-card-content">
            <div className="ewa-breed-matrix">
              {Object.entries(selectedFormula.breed_performance)
                .sort(([, a], [, b]) => b - a)
                .map(([breed, rate]) => (
                  <div key={breed} className="ewa-breed-item">
                    <div className="ewa-breed-name">{breed}</div>
                    <div className="ewa-breed-bar">
                      <div className="ewa-breed-bar-fill" style={{ width: `${Math.min(rate, 100)}%` }} />
                    </div>
                    <div className="ewa-breed-rate">{rate.toFixed(1)}%</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EggWatchFeedPerformance;
